import {
    FlexiCancelButton,
    FormPageLayout,
    PlatformLoader,
} from '@flexinet/ui-components';
import { PlatformServicePlan } from '@flexinet/pricing-components';
import {
    Badge,
    Box,
    Breadcrumbs,
    Card,
    Group,
    SimpleGrid,
    Stack,
    Text,
} from '@mantine/core';
import {
    NavLink,
    useNavigate,
    useParams
} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {useSubscriptions} from '../../subscriptions/hooks/useSubscriptions';
import {cancelSubscriptionRecord} from '../../subscriptions/services/SubscriptionsService';
import {notifications} from '@mantine/notifications';
import {useState} from 'react';
import {useUserAccount} from '../../../../hooks/useUserAccount';
import {Product, ProductStatus, toServicePlan} from '../types';
import type {Price} from '../../prices/types';
import type {SubscriptionItem} from '../../subscriptions/types';
import {stage} from '../../../../config/stage';

import {fetchProduct} from '../services/ProductsService';
import {fetchPricesByProduct} from '../../prices/services/PricesService';
import {
    CancelSubscriptionButton,
    SubscribeButton
} from '../buttons';
import {BillingSetupCard} from '../../customers/components/BillingSetupCard';

export const ProductDetailPage = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {userAccount: account} = useUserAccount();
    const [cancellingPriceId, setCancellingPriceId] = useState<string | null>(null);

    const {
        data: product,
        isLoading: productLoading
    } = useQuery({
                     queryKey: ['product', id],
                     queryFn: () => fetchProduct(id as string),
                     enabled: !!id
                 });

    const {
        data: prices,
        isLoading: pricesLoading
    } = useQuery({
                     queryKey: ['prices', id],
                     queryFn: () => fetchPricesByProduct(id as string),
                     enabled: !!id
                 });

    const {
        items: subscriptions,
        loading: subscriptionsLoading,
        refetch: refetchSubscriptions
    } = useSubscriptions();


    const subscribedPriceIds = subscriptions
        ?.filter(sub => sub.status === 'Active')
        ?.flatMap(sub => sub.items?.items?.map(item => item.priceId) || []) || [];

    const handleSubscribe = async (price: Price) => {
        const existingCustomerId = account?.customerId;

        if (!existingCustomerId) {
            notifications.show({
                title: 'Billing Setup Required',
                message: 'Please set up your billing profile using the banner above before subscribing.',
                color: 'orange',
            });
            return;
        }

        navigate(`/products/subscribe?productId=${id}&priceId=${price.id}&returnTo=${encodeURIComponent(`/products/${id}`)}`);
    };

    const handleCancelSubscription = async (priceId: string) => {
        setCancellingPriceId(priceId);

        try {
            const subscription = subscriptions.find(sub =>
                                                        sub.items?.items?.some((item: SubscriptionItem) => item.priceId === priceId)
            );

            if (subscription?.id) {
                const result = await cancelSubscriptionRecord(subscription.id);

                if (result) {
                    refetchSubscriptions();
                    notifications.show({
                        title: 'Success',
                        message: 'Subscription cancelled successfully',
                        color: 'green'
                    });
                }
            }
        } catch (error) {
            notifications.show({
                                   title: 'Error',
                                   message: 'Failed to cancel subscription',
                                   color: 'red'
                               });
        } finally {
            setCancellingPriceId(null);
        }
    };

    const EditPageNavigation = ({product}: { product?: Product | null }) => {
        const breadcrumbs = [
            {
                title: 'Home',
                href: '/dashboard'
            },
            {
                title: 'Products',
                href: '/products'
            },
            {title: product?.name || id}
        ].map((item, index) => (
            <Box key={index}>
                {item.href ? <NavLink to={item.href}>{item.title}</NavLink> : item.title}
            </Box>
        ));

        return (
            <Box p={'xs'}>
                <Breadcrumbs separator='→' mt='xs'>{breadcrumbs}</Breadcrumbs>
            </Box>
        );
    };

    const tools = [
        <FlexiCancelButton key={'close'} onClick={() => navigate('/products')}/>
    ];

    if (productLoading || pricesLoading || subscriptionsLoading) {
        return <PlatformLoader message='Loading product details...Please wait'/>;
    }

    const plan = product ? toServicePlan(product, prices?.map(p => ({ amount: p.amount, billingPeriod: p.billingPeriod }))) : null;

    return (
        <FormPageLayout
            title={product?.name || 'Product Details'}
            pageNavigation={<EditPageNavigation product={product}/>}
            tools={tools}
        >
            <Stack gap="lg">
                {account && !account.customerId && (
                    <BillingSetupCard variant="banner" />
                )}
                {product && (
                    <Card withBorder p="xl">
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Text fw={700} size="xl">{product.name}</Text>
                                <Badge color={product.status === ProductStatus.Active ? 'green' : 'gray'}>
                                    {product.status}
                                </Badge>
                            </Group>

                            {product.shortDescription && (
                                <Text size="sm" c="dimmed">{product.shortDescription}</Text>
                            )}
                        </Stack>
                    </Card>
                )}

                {plan && prices && prices.length > 0 && (
                    <Card withBorder p="xl">
                        <Stack gap="md">
                            <Text fw={500} size="lg">Available Plans</Text>
                            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
                                {prices.map((price: Price) => {
                                    const isSubscribed = subscribedPriceIds.includes(price.id);
                                    const pricePlan = {
                                        ...plan,
                                        id: price.id,
                                        name: price.description || price.billingPeriod,
                                        prices: [{ amount: price.amount, billingPeriod: price.billingPeriod }],
                                    };
                                    return (
                                        <PlatformServicePlan
                                            key={price.id}
                                            plan={pricePlan}
                                            stage={stage}
                                            sections={{
                                                action: isSubscribed ? (
                                                    <CancelSubscriptionButton
                                                        onClick={() => handleCancelSubscription(price.id)}
                                                        loading={cancellingPriceId === price.id}
                                                    />
                                                ) : (
                                                    <SubscribeButton
                                                        onClick={() => handleSubscribe(price)}
                                                    />
                                                ),
                                            }}
                                        />
                                    );
                                })}
                            </SimpleGrid>
                        </Stack>
                    </Card>
                )}
            </Stack>
        </FormPageLayout>
    );
};
